/* eslint-disable indent*/
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Spinner } from '@/components/generic';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { IMAGE, UPLOAD_PICKER_TYPES, VIDEO } from '@/constants';
import { groupBy } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { UploadContentLayout } from '@/layout';
import { UploadContentSection } from '@/pages';
import { UploadPhotosPlaceholder as ImageIcon, TributeVideoIcon } from '@/resources';
import {
  useDeleteAssetMutation,
  useGetEventAssets,
  useGetEventByIdQuery,
  useGetEventTags
} from '@/services';
import { Asset, AssetSlot, Tag } from '@/types';

export const UploadContentPhotosVideos = () => {
  const [t] = useTranslation('common');
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [shouldRefetch, setShouldRefetch] = useState<boolean>(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);

  const { mutate: deleteAsset } = useDeleteAssetMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('assetSuccessfullyDeleted'));
      setIsOpenDeleteModal(false);
    },
    onError: (error) => {
      if (error.response.data.ErrorMessages[0].Code === 'ArgumentNull') {
        showToast(ToastType.ERROR, t('userDeleteAssetError'));
      } else {
        showToast(ToastType.ERROR, t('assetDeleteError'));
      }
    }
  });
  const { isLoading: isLoadingTags, data: allTags } = useGetEventTags(eventId ?? '');
  const { data: eventData, isLoading: isEventLoading } = useGetEventByIdQuery(Number(eventId));
  const {
    data: allAssets,
    isLoading: isAssetsLoading,
    refetch
  } = useGetEventAssets(
    eventId ?? '',
    allTags?.data
      ?.filter((tag: Tag) => [IMAGE, VIDEO].includes(tag.name))
      .map((tag: Tag) => `${tag.id}`) ?? [],
    0,
    '',
    undefined,
    undefined,
    !isLoadingTags && !!allTags
  );

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      setShouldRefetch(false);
    }
  }, [shouldRefetch]);

  const getPhotosAndVideos = () => {
    const result = [];
    const assets = allAssets?.pages
      .flatMap((page) => page.data?.items)
      .filter(
        (asset: Asset) =>
          asset.tags.length > 0 &&
          asset.tags.some((tag) => tag.name === IMAGE || tag.name === VIDEO)
      );
    const groupedAssets = groupBy(assets ?? [], ({ assetType }: Asset) => assetType);

    for (const [key, value] of groupBy(
      eventData?.data.assetSlots,
      ({ slotType }: AssetSlot) => slotType?.assetType
    )) {
      if ([IMAGE, VIDEO].includes(key as string)) {
        result.push(
          <div key={key} className={`${key === VIDEO && 'pt-[25px]'}`}>
            <UploadContentSection
              eventId={eventId ?? ''}
              organizationId={eventData?.data?.organization?.id}
              sectionTitle={key === IMAGE ? 'photos' : 'videos'}
              sectionText={
                key === IMAGE
                  ? value.length === 1
                    ? 'pleaseAddPhoto'
                    : 'pleaseAddPhotos'
                  : 'pleaseAddVideos'
              }
              icon={
                key === IMAGE ? (
                  <ImageIcon className='text-[22px]' />
                ) : (
                  <TributeVideoIcon fill='var(--color-secondary-gray)' className='size-[22px]' />
                )
              }
              uploadButtonText={key === IMAGE ? 'uploadPhotos' : 'uploadVideos'}
              assetTag={key === IMAGE ? IMAGE : VIDEO}
              pickerTypes={key === IMAGE ? UPLOAD_PICKER_TYPES.IMAGE : UPLOAD_PICKER_TYPES.VIDEO}
              numberOfAssetSlots={value.length}
              assets={groupedAssets.get(key as string) ?? []}
              setShouldRefetch={setShouldRefetch}
              setSelectedAsset={setSelectedAsset}
              setIsDeleteOpen={setIsOpenDeleteModal}
              selectedAsset={selectedAsset}
            />
          </div>
        );
      }
    }

    return result;
  };

  return (
    <UploadContentLayout goBack={() => navigate(`/events/${eventId}/upload-content/requests`)}>
      {(isEventLoading || isAssetsLoading || isLoadingTags) && <Spinner />}
      {!isEventLoading &&
        !isAssetsLoading &&
        !isLoadingTags &&
        eventData &&
        allAssets &&
        allTags && (
          <>
            <div className='grid max-w-[960px] grid-cols-1'>{getPhotosAndVideos()}</div>
            {isOpenDeleteModal && selectedAsset && (
              <div className='fixed left-0 top-0 z-50 size-full'>
                <ConfirmationModal
                  isOpen={true}
                  message={t('deleteMessage', { item: selectedAsset?.assetName })}
                  onClose={() => setIsOpenDeleteModal(false)}
                  onConfirm={() => deleteAsset(selectedAsset?.id)}
                />
              </div>
            )}
          </>
        )}
    </UploadContentLayout>
  );
};
